/**
 * @fileoverview Mock backend for the shared Target resource.
 * @description Registers GET /sales/targets (list, with filters; a paged request is the backend's
 * My Page list) and GET /sales/targets/:id (the backend's Get Target Details), both answered by
 * my-page-mock.service.ts, and the target mutations (PATCH /sales/targets/:id for Edit
 * Target's name/period/priority, PATCH .../stage, PATCH .../reassign, PATCH .../follow-up, POST
 * .../confirm-payment) on the feature's shared mock adapter (see
 * sales-mock-adapter.ts), backed by targets-mock-store.ts. New, additive API surface — see
 * tenants-mock.service.ts's file header for why no existing page/hook is wired to it yet.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { myPageDetailsReply, myPageListReply } from './my-page-mock.service'
import { salesMockAdapter } from './sales-mock-adapter'
import {
  confirmTargetPayment,
  listTargets,
  reassignTarget,
  scheduleFollowUp,
  updateTargetDetails,
  updateTargetStage,
} from './targets-mock-store'

import './sales-targets-mock.service'

import type {
  ReassignTargetInput,
  ScheduleFollowUpInput,
  TargetFilters,
  UpdateStageInput,
  UpdateTargetDetailsInput,
} from './targets-mock-store'

/**
 * @description Parses the boolean-valued `overdue` query param, which arrives as the string "true"
 * or "false" over HTTP (or is absent entirely).
 * @param {string | undefined} value - The raw query param value.
 * @returns {boolean | undefined} The parsed boolean, or undefined when the param was absent.
 */
function parseOverdueParam(value: string | undefined): boolean | undefined {
  if (value === undefined) return undefined
  return value === 'true'
}

if (salesMockAdapter) {
  salesMockAdapter.onGet('/sales/targets').reply((config) => {
    // The backend's My Page list shares this URL with the mock-only per-tenant list below; only
    // the My Page list is paged, so a request that carries `page` is answered by my-page-mock.
    if ((config.params as { page?: unknown } | undefined)?.page !== undefined) {
      return myPageListReply(config)
    }
    const { ownerIds, overdue, ...rest } = (config.params ?? {}) as Partial<TargetFilters> & {
      ownerIds?: string
      overdue?: string
    }
    const parsedOverdue = parseOverdueParam(overdue)
    const filters: TargetFilters = {
      ...rest,
      ...(ownerIds ? { ownerIds: ownerIds.split(',') } : {}),
      ...(parsedOverdue !== undefined ? { overdue: parsedOverdue } : {}),
    }
    return [200, listTargets(filters)]
  })

  // GET /sales/targets/:id is the backend's Get Target Details (3.5.11.17), answered by my-page-mock.
  // It replaces the per-tenant Target lookup this route used to serve, which no page ever called.
  salesMockAdapter.onGet(/^\/sales\/targets\/[^/]+$/).reply(myPageDetailsReply)

  salesMockAdapter.onPatch(/^\/sales\/targets\/[^/]+\/stage$/).reply((config) => {
    const id = config.url?.split('/').at(-2)
    const input = JSON.parse(config.data as string) as UpdateStageInput
    const result = id ? updateTargetStage(id, input) : null

    if (!result) {
      return [404, { appCode: '1404', message: 'Target not found.' }]
    }
    if ('error' in result) {
      // 422, not 403: the shared response interceptor redirects the whole app to the full-page
      // /403 screen for any status-403 response whose appCode isn't registered in
      // APP_ERROR_CODE_MAP (see response.interceptor.ts's redirectOnFatalStatus) — right for "you
      // may not open this page at all", wrong for a business-rule validation failure on a form the
      // caller should stay on (wrong actor, missing reason note, or a direct Active Client set).
      return [422, { appCode: '1422', message: result.error }]
    }
    return [200, result]
  })

  salesMockAdapter.onPatch(/^\/sales\/targets\/[^/]+\/reassign$/).reply((config) => {
    const id = config.url?.split('/').at(-2)
    const input = JSON.parse(config.data as string) as ReassignTargetInput
    const result = id ? reassignTarget(id, input) : null

    if (!result) {
      return [404, { appCode: '1404', message: 'Target not found.' }]
    }
    if ('error' in result) {
      // 422, not 403 — see the stage handler's comment above for why.
      return [422, { appCode: '1422', message: result.error }]
    }
    return [200, result]
  })

  salesMockAdapter.onPatch(/^\/sales\/targets\/[^/]+$/).reply((config) => {
    const id = config.url?.split('/').at(-1)
    const input = JSON.parse(config.data as string) as UpdateTargetDetailsInput
    const result = id ? updateTargetDetails(id, input) : null

    if (!result) {
      return [404, { appCode: '1404', message: 'Target not found.' }]
    }
    if ('error' in result) {
      // 422, not 403 — see the stage handler's comment above for why.
      return [422, { appCode: '1422', message: result.error }]
    }
    return [200, result]
  })

  salesMockAdapter.onPatch(/^\/sales\/targets\/[^/]+\/follow-up$/).reply((config) => {
    const id = config.url?.split('/').at(-2)
    const input = JSON.parse(config.data as string) as ScheduleFollowUpInput
    const result = id ? scheduleFollowUp(id, input) : null

    if (!result) {
      return [404, { appCode: '1404', message: 'Target not found.' }]
    }
    if ('error' in result) {
      // 422, not 403 — see the stage handler's comment above for why.
      return [422, { appCode: '1422', message: result.error }]
    }
    return [200, result]
  })

  salesMockAdapter.onPost(/^\/sales\/targets\/[^/]+\/confirm-payment$/).reply((config) => {
    const id = config.url?.split('/').at(-2)
    const { confirmedByUserId } = JSON.parse(config.data as string) as { confirmedByUserId: string }
    const updated = id ? confirmTargetPayment(id, confirmedByUserId) : null

    if (!updated) {
      return [404, { appCode: '1404', message: 'Target not found.' }]
    }
    return [200, updated]
  })
}
